import type { FC } from 'react';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import Pack from 'components/Pack';
import PopUp from 'components/PopUp';
import { PackRarity } from 'global/constants';
import { trackEvent } from 'global/firebase';
import { RouteTypes } from 'RouteTypes';
import { selectAuthAuthorized } from 'store/auth/authSelectors';
import { setLoginModal } from 'store/login/loginActions';

import PackButton from './PackButton';
import {
  CountdownHeadline,
  CountdownNumberBox,
  CountdownNumberLabel,
  CountdownNumberRow,
  CountdownWrapper,
  PopUpText,
  StyledPackList,
  StyledWrapper,
} from './styled';
import type { IPack } from './types';

const PackList: FC<IPack> = ({
  pricing,
  id,
  $refCallback,
  packCounter,
  creator,
  title,
  isSaleFinished,
  isSaleStarted,
  releaseDate,
  setIsSaleStarted,
  packDistribution,
  rarities,
}) => {
  const history = useHistory();
  const dispath = useDispatch();
  const { t } = useTranslation();
  const [diff, setDiff] = useState<Duration | null>(null);
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const isAuthorized = useSelector(selectAuthAuthorized);
  const handleBuy = (collectionId: string | undefined, rarity: string | undefined) => {
    if (!isAuthorized) {
      dispath(setLoginModal({ isOpen: true }));
    } else if (!isSaleStarted) {
      setShowCountdownModal(true);
    } else {
      const savedState = window.history.state;
      window.history.replaceState({ ...savedState, scrollTo: `pack-btn-${id}/${PackRarity.common}` }, '');
      history.push(`${RouteTypes.PackPurchase}/${collectionId}/${rarity}`);
    }

    trackEvent('buy_pack_clicked');
  };
  const hideCountdownModal = () => setShowCountdownModal(false);

  useEffect(() => {
    if (isSaleStarted || !releaseDate) {
      return undefined;
    }

    const startMoment: dayjs.Dayjs = dayjs(releaseDate);
    let interval: ReturnType<typeof setTimeout>;

    const onTimer = () => {
      const newDuration = dayjs.duration(startMoment.diff(dayjs()));

      if (newDuration.asSeconds() <= 0) {
        setDiff(null);
        clearInterval(interval);

        if (setIsSaleStarted) setIsSaleStarted(true);
      } else {
        setDiff(newDuration);
      }
    };

    interval = setInterval(onTimer, 1000);
    onTimer();

    return () => clearInterval(interval);
  }, []);

  const diffData = diff && [
    [`0${diff.get('days').toString()}`.slice(-2), t('common.intervals.days')],
    [`0${diff.get('hours').toString()}`.slice(-2), t('common.intervals.hours')],
    [`0${diff.get('minutes').toString()}`.slice(-2), t('common.intervals.minutes')],
    [`0${diff.get('seconds').toString()}`.slice(-2), t('common.intervals.seconds')],
  ];

  let prices = { ...pricing };
  const preferredToken = pricing.preferred_token || 'FUSD';
  if (preferredToken === 'FLOW' && pricing.options && pricing.options.FLOW) {
    prices = { ...prices, ...pricing.options.FLOW };
  }

  return (
    <StyledWrapper id="packs" ref={$refCallback}>
      <StyledPackList data-test-id="pack_list">
        {rarities?.map((rarity) => (
          <Pack
            key={rarity}
            packType={rarity}
            price={prices[rarity]}
            currency={preferredToken}
            packsLeft={packCounter?.[`${rarity}_left`]}
            packsSold={packCounter?.[`${rarity}_sold`]}
            isSaleFinished={isSaleFinished}
            isSaleStarted={isSaleStarted}
            creator={creator}
            title={title}
            packDistribution={packDistribution}
            rarity={rarity}
            data-test-id={`${rarity}_pack`}
          >
            <PackButton
              data-test-id="pack_button"
              total={packCounter?.[`${rarity}_left`]}
              isSaleFinished={isSaleFinished}
              onClick={() => handleBuy(id, rarity)}
            />
          </Pack>
        ))}
      </StyledPackList>
      {showCountdownModal && diffData && (
        <PopUp onHide={hideCountdownModal}>
          <PopUpText>
            {t('collection.sale.countdownTitle')}
          </PopUpText>
          <CountdownWrapper>
            <CountdownHeadline>{t('collection.sale.countdownSubtitle')}</CountdownHeadline>
            <CountdownNumberRow>
              {diffData.map((item) => (
                <div key={item[1]}>
                  <CountdownNumberBox>{item[0]}</CountdownNumberBox>
                  <CountdownNumberLabel>{item[1]}</CountdownNumberLabel>
                </div>
              ))}
            </CountdownNumberRow>
          </CountdownWrapper>
          <Button
            onClick={hideCountdownModal}
            background="black"
            width="100%"
            size="big"
          >
            Ok
          </Button>
        </PopUp>
      )}
    </StyledWrapper>
  );
};

export default PackList;
